/**
 * Render-based geometry lint rules. Each is a pure function of the frame snapshots on the runtime
 * context (measured element boxes/opacity/appearance per probe point) — no timeline driving, no DOM
 * mutation. Thresholds are the codeo layout-measurer's, ported verbatim.
 */
import { describeElement, visibleThroughWindows } from './measure';
import type { ElementMeasurement, FrameSnapshot, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';
import { findNearAlignedPairs, type AlignmentEntry } from './alignment';
import { isCompositionFrame, isPlacedByData } from './dataRules';

const OPACITY_PRESENT_THRESHOLD = 0.05;
const TEXT_UNREADABLE_PCT = 2.5;
const TEXT_TOO_SMALL_PCT = 4.5;
const SAFE_MARGIN = 80;
const FULL_BLEED_THRESHOLD = 0.92;
const RECT_EPS = 0.5;
const OVERLAP_TOL_PX = 4;
const MIN_ALIGN_SIZE_PX = 24;

// An element (or a group, via an ancestor) the author has marked as deliberately reaching past the
// canvas edge: a fly-on/fly-off, a mid-scene exit, a scatter that bleeds off the frame. The bounds
// rules skip it entirely. The failure these rules exist to catch is content left outside the frame by
// accident, so an explicit "yes, I meant this" is the whole signal they need — anything narrower
// (a window, a phase) also rules out intents that are perfectly reasonable.
//
// The one carrier that says nothing is the composition root: coincident with the canvas, it reaches
// past no edge, so the mark there only silences every element beneath it. `allowOffscreenOnFrame`
// reports that, and the bounds rules look straight through it.
const ALLOW_OFFSCREEN_ATTR = '[data-l5-allow-offscreen]';

// The escape hatch, phrased for each of the two rules that honour it.
const OFFSCREEN_HATCH_OVERFLOW =
    'If reaching past the edge is the intent — a fly-on/fly-off, or a deliberate bleed — add ' +
    'data-l5-allow-offscreen to that element (or to a wrapper around just that group, never one ' +
    'spanning the frame) and this rule will leave it alone.';
const OFFSCREEN_HATCH_MARGIN =
    'If it is meant to reach past the edge, add data-l5-allow-offscreen to that element (or to a ' +
    'wrapper around just that group, never one spanning the frame).';

// The overlap of two rects on each axis; negative when they don't intersect on that axis.
function overlapExtent(a: Rect, b: Rect): { width: number; height: number } {
    const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
    return { width, height };
}

function isFullBleed(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    return (
        rect.width >= canvasWidth * FULL_BLEED_THRESHOLD &&
        rect.height >= canvasHeight * FULL_BLEED_THRESHOLD
    );
}

function overflowsCanvas(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    return (
        rect.x < -RECT_EPS ||
        rect.y < -RECT_EPS ||
        rect.x + rect.width > canvasWidth + RECT_EPS ||
        rect.y + rect.height > canvasHeight + RECT_EPS
    );
}

// True when an edge floats inside the safe-margin gutter: strictly inside the canvas (not flush to
// the edge, which reads as intentional bleed) but closer than SAFE_MARGIN to it.
function intrudesGutter(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    const gaps = [
        rect.x,
        rect.y,
        canvasWidth - (rect.x + rect.width),
        canvasHeight - (rect.y + rect.height),
    ];
    return gaps.some((gap) => gap > RECT_EPS && gap < SAFE_MARGIN);
}

interface ElementPresence {
    element: Element;
    /** This element's measurements across the frames where it is actually visible. */
    frames: ElementMeasurement[];
}

// Group measurements by element identity (the same Element object recurs across frames, since every
// probe measures the one loaded document), keeping only frames where the element is visible.
function presentElements(context: RuntimeLintContext): ElementPresence[] {
    const byElement = new Map<Element, ElementMeasurement[]>();
    for (const frame of context.frames ?? []) {
        for (const measurement of frame.elements) {
            if (measurement.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            const list = byElement.get(measurement.element) ?? [];
            list.push(measurement);
            byElement.set(measurement.element, list);
        }
    }
    return [...byElement].map(([element, frames]) => ({ element, frames }));
}

/**
 * The smallest rendered text this element ever shows, in px and as a % of canvas height, or null when
 * it renders no measurable text. Both units are reported: the percentage is the rule's threshold, but
 * px is what the author actually types.
 */
function minRenderedText(
    presence: ElementPresence,
    canvasHeight: number,
): { px: number; pct: number } | null {
    const sizes = presence.frames
        .filter((m) => m.hasText && m.fontSizePx > 0)
        .map((m) => m.fontSizePx);
    if (!sizes.length) {
        return null;
    }
    const px = Math.min(...sizes);
    return { px, pct: (px / canvasHeight) * 100 };
}

/**
 * The smallest font size in whole px that satisfies `pctThreshold` on this canvas. Rounded up, so an
 * author who sets exactly this number clears the threshold rather than landing a fraction under it.
 */
function thresholdPx(pctThreshold: number, canvasHeight: number): number {
    return Math.ceil((pctThreshold / 100) * canvasHeight);
}

/**
 * Text rendered below TEXT_UNREADABLE_PCT of the canvas height is illegible at normal viewing
 * distances. This is a hard floor — even ambient text (watermarks, credits) must clear it.
 */
export const textBelowMinSize: RuntimeLintRule = {
    id: 'text-below-min-size',
    severity: 'error',
    guidance: `Render every on-screen text element at no less than ${TEXT_UNREADABLE_PCT}% of the canvas height — below that it is unreadable at normal viewing distances. This floor applies even to ambient text.`,
    check(context: RuntimeLintContext): string[] {
        const canvasHeight = context.canvasSize?.height;
        if (!canvasHeight) return [];
        const floorPx = thresholdPx(TEXT_UNREADABLE_PCT, canvasHeight);
        const messages: string[] = [];
        for (const presence of presentElements(context)) {
            const size = minRenderedText(presence, canvasHeight);
            if (size !== null && size.pct < TEXT_UNREADABLE_PCT) {
                messages.push(
                    `${describeElement(presence.element)} renders text at ${Math.floor(size.px)}px ` +
                        `(${size.pct.toFixed(2)}% of this ${canvasHeight}px-tall canvas) — below the ` +
                        `${TEXT_UNREADABLE_PCT}% readable floor. Raise its font size to at least ` +
                        `${floorPx}px.`,
                );
            }
        }
        return messages;
    },
};

/**
 * Text in the [TEXT_UNREADABLE_PCT, TEXT_TOO_SMALL_PCT) band is legible but smaller than the norm
 * for motion-graphic body text — an advisory warning. Deliberately subtle text (watermark,
 * attribution, disclaimer, footer credit) can opt out with `data-l5-text-role="ambient"`. Text
 * already below the unreadable floor is left to text-below-min-size, so the two never double-report.
 */
export const textBelowRecommendedSize: RuntimeLintRule = {
    id: 'text-below-recommended-size',
    severity: 'warning',
    guidance: `Aim for on-screen text at ${TEXT_TOO_SMALL_PCT}% of the canvas height or larger. If a piece is intentionally subtle (watermark, attribution, disclaimer, footer credit), mark it \`data-l5-text-role="ambient"\` to silence this.`,
    check(context: RuntimeLintContext): string[] {
        const canvasHeight = context.canvasSize?.height;
        if (!canvasHeight) return [];
        const normPx = thresholdPx(TEXT_TOO_SMALL_PCT, canvasHeight);
        const messages: string[] = [];
        for (const presence of presentElements(context)) {
            const size = minRenderedText(presence, canvasHeight);
            if (size === null || size.pct < TEXT_UNREADABLE_PCT || size.pct >= TEXT_TOO_SMALL_PCT) {
                continue;
            }
            if (presence.element.closest('[data-l5-text-role="ambient"]')) continue;
            messages.push(
                `${describeElement(presence.element)} renders text at ${Math.floor(size.px)}px ` +
                    `(${size.pct.toFixed(2)}% of this ${canvasHeight}px-tall canvas), below the ` +
                    `${TEXT_TOO_SMALL_PCT}% norm for motion-graphic text — that would be ${normPx}px ` +
                    `here. Increase the font size, or mark it data-l5-text-role="ambient" if it is ` +
                    `intentionally subtle.`,
            );
        }
        return messages;
    },
};

/**
 * Whether this element's nearest `data-l5-allow-offscreen` carrier is one the bounds rules honour.
 * A carrier spanning the frame is not: see `ALLOW_OFFSCREEN_ATTR`. Only the nearest one is
 * consulted, so a real group mark still works inside a frame-spanning one.
 */
function hasHonouredOffscreenMark(
    element: Element,
    frame: FrameSnapshot,
    rectOf: () => Map<Element, Rect>,
    canvasWidth: number,
    canvasHeight: number,
): boolean {
    const carrier = element.closest(ALLOW_OFFSCREEN_ATTR);
    if (!carrier) return false;
    return !isTheFrame(carrier, frame, rectOf(), canvasWidth, canvasHeight);
}

/**
 * A carrier that IS the frame: coincident with the canvas, and holding every element in the
 * composition.
 *
 * Both halves are load-bearing, because each alone catches markup that legitimately carries the
 * mark. Size alone (`isFullBleed`) has no position term, so it also matches a travel track — a
 * `whip-stage` or a `world` several frames wide, which reaches past the edge exactly as the mark
 * says. Geometry alone still cannot separate the root from a frame-sized decorative layer like an
 * `inset: 0` particle field, whose dots are meant to bleed; what separates them is that the root
 * holds the composition's actual content, and the field sits beside it.
 */
function isTheFrame(
    carrier: Element,
    frame: FrameSnapshot,
    rectOf: Map<Element, Rect>,
    canvasWidth: number,
    canvasHeight: number,
): boolean {
    const tag = carrier.tagName.toLowerCase();
    if (tag === 'body' || tag === 'html') return true;
    const rect = rectOf.get(carrier);
    if (rect === undefined) return false;
    return (
        coversCanvasExactly(rect, canvasWidth, canvasHeight) && holdsEveryElement(carrier, frame)
    );
}

/** Covers the canvas without reaching past it — the frame itself, not a track that travels. */
function coversCanvasExactly(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    return (
        isFullBleed(rect, canvasWidth, canvasHeight) &&
        rect.x >= -RECT_EPS &&
        rect.y >= -RECT_EPS &&
        rect.x + rect.width <= canvasWidth + RECT_EPS &&
        rect.y + rect.height <= canvasHeight + RECT_EPS
    );
}

/** Whether every other measured element sits inside this one, making it the composition's root. */
function holdsEveryElement(carrier: Element, frame: FrameSnapshot): boolean {
    return frame.elements.every(
        (measurement) => measurement.element === carrier || carrier.contains(measurement.element),
    );
}

/** Every element measured in this frame, by element, so a carrier's box can be looked up. */
function rectsByElement(frame: FrameSnapshot): Map<Element, Rect> {
    return new Map(frame.elements.map((measurement) => [measurement.element, measurement.rect]));
}

// Visit each present element measurement once, in frame order, reporting each element at most once
// (the first frame that trips `hit`). Used by the per-frame geometry rules.
function firstOffendingFrame(
    context: RuntimeLintContext,
    hit: (rect: Rect, canvasWidth: number, canvasHeight: number) => boolean,
): Array<{ element: Element; progress: number }> {
    const size = context.canvasSize;
    if (!size) return [];
    const reported = new Set<Element>();
    const results: Array<{ element: Element; progress: number }> = [];
    for (const frame of context.frames ?? []) {
        let rects: Map<Element, Rect> | undefined;
        const rectOf = () => (rects ??= rectsByElement(frame));
        for (const measurement of frame.elements) {
            if (measurement.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            if (reported.has(measurement.element)) continue;
            // Judged on what a window inside the frame lets through, so a strip parked mostly
            // outside its own window is not reported for the part nobody can see.
            const visible = visibleThroughWindows(measurement.rect, measurement.clipRect, size);
            if (visible.width <= 0 || visible.height <= 0) continue;
            if (!hit(visible, size.width, size.height)) continue;
            // Checked only once the frame already offends, so the ancestor walk costs nothing on the
            // elements — nearly all of them — that are sitting where they should.
            if (
                hasHonouredOffscreenMark(
                    measurement.element,
                    frame,
                    rectOf,
                    size.width,
                    size.height,
                )
            ) {
                continue;
            }
            reported.add(measurement.element);
            results.push({ element: measurement.element, progress: frame.progress });
        }
    }
    return results;
}

/**
 * `data-l5-allow-offscreen` on the composition root. The mark exempts its whole subtree from the
 * bounds rules, so there it turns them off for every element at once, and it says nothing about
 * the root itself: a box coincident with the canvas reaches past no edge. It is what gets reached
 * for when a frame renders wrong and the layout is not the thing that is wrong.
 *
 * A wrapper genuinely larger than the frame — a pan or whip track — is a different shape and is
 * left alone; see `isTheFrame`.
 */
function rootCarrierMessage(element: Element): string {
    return (
        `${describeElement(element)} is the composition root — coincident with the canvas, ` +
        `holding every element — so data-l5-allow-offscreen on it reaches past no edge and only ` +
        `exempts the whole scene from the bounds rules. Move the mark onto the element that ` +
        `actually leaves the frame, and fix whatever is genuinely sitting off-canvas.`
    );
}

export const allowOffscreenOnFrame: RuntimeLintRule = {
    id: 'allow-offscreen-on-frame',
    severity: 'error',
    guidance:
        'Put `data-l5-allow-offscreen` on the element that actually leaves the frame, or on a ' +
        'wrapper around just that group — never on the composition root. A root coincident with ' +
        'the canvas reaches past no edge, so the mark says nothing about it and only exempts every ' +
        'element in the scene from the bounds rules. A wrapper genuinely wider than the frame, ' +
        'like a pan or whip track, is a real carrier and is fine.',
    check(context: RuntimeLintContext): string[] {
        const size = context.canvasSize;
        if (!size) return [];
        const reported = new Set<Element>();
        const messages: string[] = [];
        // `<body>`/`<html>` are roots by definition but never measured — `measureElements` walks
        // `body.querySelectorAll('*')` — so the frame loop below would never see them, and the mark
        // would silence the bounds rules with nothing said about why.
        for (const root of [context.document.documentElement, context.document.body]) {
            if (root?.matches(ALLOW_OFFSCREEN_ATTR)) {
                reported.add(root);
                messages.push(rootCarrierMessage(root));
            }
        }
        for (const frame of context.frames ?? []) {
            let rects: Map<Element, Rect> | undefined;
            for (const measurement of frame.elements) {
                const element = measurement.element;
                if (reported.has(element)) continue;
                if (!element.matches(ALLOW_OFFSCREEN_ATTR)) continue;
                rects ??= rectsByElement(frame);
                if (!isTheFrame(element, frame, rects, size.width, size.height)) continue;
                reported.add(element);
                messages.push(rootCarrierMessage(element));
            }
        }
        return messages;
    },
};

/**
 * An element whose rendered box extends past the canvas edge (and is not a deliberate full-bleed
 * background) has content clipped off-frame. Reported at the first frame it overflows, unless it is
 * marked `data-l5-allow-offscreen`.
 */
export const contentOverflowsCanvas: RuntimeLintRule = {
    id: 'content-overflows-canvas',
    severity: 'error',
    guidance:
        'Keep on-screen content within the canvas bounds. A full-bleed background is fine, but any ' +
        'other element extending past the edge is clipped off-frame. Mark an element ' +
        '`data-l5-allow-offscreen` when reaching past the edge is the design — it flies in or out of ' +
        'frame, or it is a scatter meant to bleed off — and this rule will not judge its bounds.',
    check(context: RuntimeLintContext): string[] {
        const offenders = firstOffendingFrame(
            context,
            (rect, w, h) => overflowsCanvas(rect, w, h) && !isFullBleed(rect, w, h),
        );
        return offenders.map(
            ({ element, progress }) =>
                `${describeElement(element)} extends past the canvas edge at progress ` +
                `${progress} — content is clipped off-frame. Move or resize it to sit within the ` +
                `canvas. ${OFFSCREEN_HATCH_OVERFLOW}`,
        );
    },
};

/**
 * An element sitting inside the canvas but within the SAFE_MARGIN gutter reads as cramped at the
 * edge. Advisory: intentional edge bleed (flush to the edge) and full-bleed backgrounds are exempt,
 * as is anything marked `data-l5-allow-offscreen` — an element allowed past the edge crosses the
 * gutter by definition, and would otherwise just trade the overflow error for this warning.
 */
export const contentInSafeMargin: RuntimeLintRule = {
    id: 'content-in-safe-margin',
    severity: 'warning',
    guidance:
        `Keep on-screen content out of the ${SAFE_MARGIN}px safe-margin gutter at the canvas edges ` +
        '(unless it is a deliberate full-bleed background, a flush edge bleed, or marked ' +
        '`data-l5-allow-offscreen`).',
    check(context: RuntimeLintContext): string[] {
        const offenders = firstOffendingFrame(
            context,
            (rect, w, h) =>
                !isFullBleed(rect, w, h) &&
                !overflowsCanvas(rect, w, h) && // overflow rule owns those
                intrudesGutter(rect, w, h),
        );
        return offenders.map(
            ({ element, progress }) =>
                `${describeElement(element)} sits inside the ${SAFE_MARGIN}px safe-margin gutter ` +
                `at progress ${progress} — inside the canvas but cramped at the edge. Move it ` +
                `further in. ${OFFSCREEN_HATCH_MARGIN}`,
        );
    },
};

/**
 * Two text glyph regions that share a screen region in the same frame — the "new text fades in
 * before the old text in the same spot fades out" failure. Text-on-text only (a shape or icon under
 * text is intentional layering). A shared or per-element `data-l5-allow-overlap` opts a deliberate
 * crossfade out. Advisory.
 */
export const textOverlap: RuntimeLintRule = {
    id: 'text-overlap',
    severity: 'warning',
    guidance:
        'Do not let two pieces of text occupy the same spot at the same time — sequence them so one ' +
        'fades out before the next reveals, or reposition them. Add data-l5-allow-overlap if the ' +
        'crossfade is intentional.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        // Stable per-element index for order-independent pair keys.
        const indexOf = new Map<Element, number>();
        const keyFor = (a: Element, b: Element): string => {
            for (const el of [a, b]) if (!indexOf.has(el)) indexOf.set(el, indexOf.size);
            const [x, y] = [indexOf.get(a)!, indexOf.get(b)!].sort((m, n) => m - n);
            return `${x}|${y}`;
        };
        for (const frame of context.frames ?? []) {
            const texts = frame.elements.filter(
                (m) => m.hasText && m.opacity >= OPACITY_PRESENT_THRESHOLD,
            );
            for (let i = 0; i < texts.length; i++) {
                for (let j = i + 1; j < texts.length; j++) {
                    const a = texts[i];
                    const b = texts[j];
                    if (
                        a.element.closest('[data-l5-allow-overlap]') ||
                        b.element.closest('[data-l5-allow-overlap]')
                    ) {
                        continue;
                    }
                    // Nested text (a parent holding mixed inline content and its inline child) always
                    // "overlaps" — the child's ink sits inside the parent's. That's one piece of copy,
                    // not two colliding, so skip ancestor/descendant pairs.
                    if (a.element.contains(b.element) || b.element.contains(a.element)) continue;
                    const overlap = overlapExtent(
                        visibleThroughWindows(a.inkRect, a.clipRect, context.canvasSize),
                        visibleThroughWindows(b.inkRect, b.clipRect, context.canvasSize),
                    );
                    if (overlap.width <= OVERLAP_TOL_PX || overlap.height <= OVERLAP_TOL_PX)
                        continue;
                    const key = keyFor(a.element, b.element);
                    if (reportedPairs.has(key)) continue;
                    reportedPairs.add(key);
                    messages.push(
                        `${describeElement(a.element)} and ${describeElement(b.element)} overlap ` +
                            `at progress ${frame.progress} — text appears in the same spot before ` +
                            `the other has cleared. Sequence or reposition them, or add ` +
                            `data-l5-allow-overlap if the crossfade is intentional.`,
                    );
                }
            }
        }
        return messages;
    },
};

/**
 * Two decorative shapes that are contained one-in-the-other and *almost* concentric — the
 * hand-computed-coordinates near-miss. Text is excluded (its box centering is a different concern),
 * as are shapes under 24px. Advisory; reported once per pair across frames.
 */
/**
 * Shapes whose box is the extent of drawn geometry rather than a placed object: where a path lands
 * inside its `<svg>` is its own coordinate space. A `<circle>` or a `<rect>` is not on this list —
 * a dial's hub and its ring are both circles, and excluding them by tag is excluding the case this
 * rule exists for, since every dial in the skill library is drawn in SVG.
 */
const PLOTTED_GEOMETRY_TAGS = new Set(['PATH', 'POLYLINE', 'POLYGON']);

/** An offset the author has declared deliberate, on the shape or a wrapper around it. */
const ALLOW_OFFSET_ATTR = '[data-l5-allow-offset]';

/** Roughly square: only then does a viewer read "inside" as "centred inside". */
const SQUARE_ASPECT_MIN = 0.8;
const SQUARE_ASPECT_MAX = 1.25;

/**
 * Past this share of the canvas an element is the stage the graphic is arranged on, whatever its
 * aspect. `isCompositionFrame` asks for nine tenths of both sides, which a stage wrapper with a
 * margin slips under — and on a portrait canvas, which is itself roughly square, that wrapper then
 * satisfies every test for a shape things are centred in.
 */
const STAGE_CANVAS_SHARE = 0.5;

export function isStage(rect: Rect, canvasSize?: { width: number; height: number }): boolean {
    if (!canvasSize) return false;
    const canvasArea = canvasSize.width * canvasSize.height;
    return canvasArea > 0 && (rect.width * rect.height) / canvasArea >= STAGE_CANVAS_SHARE;
}

export function isRoughlySquare(rect: Rect): boolean {
    if (rect.height === 0) return false;
    const aspect = rect.width / rect.height;
    return aspect >= SQUARE_ASPECT_MIN && aspect <= SQUARE_ASPECT_MAX;
}

/** A dial, a ring, a badge — a shape whose own geometry says "things centre in me". */
function readsAsRound(element: Element): boolean {
    const tag = element.tagName.toUpperCase();
    if (tag === 'CIRCLE' || tag === 'ELLIPSE') return true;
    const radius = (element as HTMLElement).style?.borderRadius ?? '';
    return /50%|9999px/.test(radius);
}

/**
 * Whether the element is turning. The transform is read live rather than from the authored source
 * because a dial hand is rotated by the timeline, not by its CSS — which also means GSAP has written
 * its own pose here, and a rotation that has landed serialises as an explicit zero.
 */
function hasRotation(element: Element): boolean {
    const transform =
        (element as HTMLElement).style?.transform ?? element.getAttribute('transform') ?? '';
    const angles = transform.match(/rotate[a-z]*\(([^)]*)\)/gi) ?? [];
    return angles.some((angle) => /[1-9]/.test(angle.replace(/rotate[a-z]*/i, '')));
}

/**
 * Two shapes contained one in the other and *almost* concentric — the hand-computed near-miss that
 * put a dial's hand, its centre dot and its ring at three different points.
 *
 * Blocking, and deliberately narrow, because those two facts depend on each other. As an advisory it
 * fired around twenty times a build and was never once acted on: a rule whose hits are mostly a
 * chart's own path "almost centred" in its own `<svg>`, or a flex row "almost centred" in the
 * composition root, teaches an author to skip all of it — including the one finding that was real.
 * So concentricity is only judged where a viewer would expect it: inside something square, round or
 * turning. Everything else reads as layout, and layout is not this rule's business.
 */
/**
 * The last probe, which is where a graphic has finished arriving. Geometry rules read only this one:
 * an entrance still in flight measures shapes at their tween size, and a relationship between two
 * of them — a baseline under bars, a dot in a ring — is not a claim about the design until both have
 * landed. Reporting it earlier is a finding about the moment.
 */
export function settledFrame(context: RuntimeLintContext): FrameSnapshot | null {
    const frames = context.frames ?? [];
    return frames.length > 0 ? frames[frames.length - 1] : null;
}

/** SVG grouping elements: their box is whatever their children span, so it is nobody's edge. */
const CONTAINER_TAGS = new Set(['G', 'SVG', 'DEFS', 'CLIPPATH', 'MASK', 'PATTERN', 'SYMBOL']);

export function isContainerElement(element: Element): boolean {
    return CONTAINER_TAGS.has(element.tagName.toUpperCase());
}

export const elementsNearAligned: RuntimeLintRule = {
    id: 'elements-near-aligned',
    severity: 'error',
    guidance:
        'When one shape sits inside a round or square one — a dial, a ring, a badge — centre it ' +
        'exactly or offset it clearly; an almost-but-not-quite-centred shape reads as a mistake. ' +
        'Put both in one coordinate frame (the same container, the same origin) rather than nudging ' +
        'one by a few pixels, or mark it data-l5-allow-offset if the offset is deliberate.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        const frame = settledFrame(context);
        if (frame) {
            const shapes = frame.elements.filter(
                (m) =>
                    !m.hasText &&
                    m.opacity >= OPACITY_PRESENT_THRESHOLD &&
                    m.rect.width >= MIN_ALIGN_SIZE_PX &&
                    m.rect.height >= MIN_ALIGN_SIZE_PX,
            );
            const entries: AlignmentEntry[] = shapes.map((m, index) => ({
                label: String(index),
                rect: m.rect,
            }));
            for (const pair of findNearAlignedPairs(entries)) {
                const innerMeasurement = shapes[Number(pair.element)];
                const outerMeasurement = shapes[Number(pair.element2)];
                const inner = innerMeasurement.element;
                const outer = outerMeasurement.element;

                // A drawn series is placed by the data, not centred by a designer — whatever it is
                // drawn inside. Said by the author's own mark, or by the shape being a run of
                // plotted geometry; a grouping element's box is an accident of what it holds.
                if (
                    PLOTTED_GEOMETRY_TAGS.has(inner.tagName.toUpperCase()) ||
                    isContainerElement(inner) ||
                    isPlacedByData(inner)
                ) {
                    continue;
                }
                // The frame the whole graphic sits in is not something things are centred in.
                if (isStage(outerMeasurement.rect, context.canvasSize)) continue;
                // Nor is a grouping element, whose centre is an accident of what it holds.
                if (isContainerElement(outer)) continue;
                // Concentricity is only a design expectation for a round, square or turning shape.
                if (
                    !isRoughlySquare(outerMeasurement.rect) &&
                    !readsAsRound(outer) &&
                    !hasRotation(inner)
                ) {
                    continue;
                }
                if (inner.closest(ALLOW_OFFSET_ATTR)) continue;

                const key = [describeElement(inner), describeElement(outer)].sort().join('|');
                if (reportedPairs.has(key)) continue;
                reportedPairs.add(key);
                messages.push(
                    `${describeElement(inner)} is almost—but not exactly—centered inside ` +
                        `${describeElement(outer)} (off by ${pair.dx}px, ${pair.dy}px). Put both in ` +
                        `one coordinate frame — the same container, the same origin — rather than ` +
                        `nudging one by a few pixels, or mark it data-l5-allow-offset if the ` +
                        `offset is deliberate.`,
                );
            }
        }
        return messages;
    },
};

/** A rule is long and thin: this is how much longer than thick it has to be. */
const RULE_ASPECT_RATIO = 10;
/** Above this a band is a panel or a bar, not a line. */
const RULE_MAX_THICKNESS_PX = 12;
/** A rule within this of a shape's edge is sitting on it — a baseline, which is correct. */
const RULE_EDGE_TOL_PX = 6;
/** How much of a shape's own width the rule has to span before it reads as crossing it. */
const RULE_OVERLAP_FRACTION = 0.5;
/** One shape crossed is a strikethrough or a divider; a series crossed is a misplaced baseline. */
const MIN_CROSSED_SHAPES = 2;

const ALLOW_INTERSECT_ATTR = '[data-l5-allow-intersect]';

/** Whether this rect is a drawn line: long, thin, and in one of the two orientations. */
function readsAsRule(rect: Rect): 'horizontal' | 'vertical' | null {
    if (rect.height <= RULE_MAX_THICKNESS_PX && rect.width >= rect.height * RULE_ASPECT_RATIO) {
        return 'horizontal';
    }
    if (rect.width <= RULE_MAX_THICKNESS_PX && rect.height >= rect.width * RULE_ASPECT_RATIO) {
        return 'vertical';
    }
    return null;
}

/**
 * A rule, axis or baseline passing *through* the shapes it should sit under.
 *
 * This is the defect that started the whole data-graphics lint effort: four bars with their feet at
 * y=900 and an orange baseline at y=820, cutting through them a fifth of the way up. It shipped with
 * "no defects found" from the reviewer, because at the size frames are reviewed an 80px error looks
 * like a design. The geometry says it plainly, which is why it belongs here and not there.
 *
 * Blocking, and narrow enough to earn that: a line crossing the middle of two or more shapes that
 * each sit mostly within its span, with room to spare on both sides so a baseline touching an edge
 * is never caught. One shape crossed is a strikethrough or a divider and is left alone.
 */
export const ruleIntersectsShapes: RuntimeLintRule = {
    id: 'rule-intersects-shapes',
    severity: 'error',
    guidance:
        'A rule, axis or baseline belongs on the edge of the shapes it serves, not through them. ' +
        'Put the rule and the shapes in one container and anchor both to the same edge rather than ' +
        'positioning each by hand — or mark the rule data-l5-allow-intersect if it is meant to ' +
        'cross them.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reported = new Set<Element>();
        const frame = settledFrame(context);
        if (frame) {
            const visible = frame.elements.filter(
                (m) =>
                    !m.hasText &&
                    m.opacity >= OPACITY_PRESENT_THRESHOLD &&
                    !isContainerElement(m.element),
            );
            for (const candidate of visible) {
                if (reported.has(candidate.element)) continue;
                const orientation = readsAsRule(candidate.rect);
                if (!orientation) continue;
                if (candidate.element.closest(ALLOW_INTERSECT_ATTR)) continue;

                const rule = candidate.rect;
                const crossed = visible.filter(({ element, rect }: ElementMeasurement) => {
                    if (element === candidate.element) return false;
                    if (
                        element.contains(candidate.element) ||
                        candidate.element.contains(element)
                    ) {
                        return false;
                    }
                    if (readsAsRule(rect)) return false;
                    // A background or a full-canvas wrapper is crossed by every rule on the page,
                    // which would make the "two shapes or it is a divider" guard meaningless.
                    if (isCompositionFrame(rect, context.canvasSize)) return false;
                    if (orientation === 'horizontal') {
                        const shared =
                            Math.min(rule.x + rule.width, rect.x + rect.width) -
                            Math.max(rule.x, rect.x);
                        if (shared < rect.width * RULE_OVERLAP_FRACTION) return false;
                        const line = rule.y + rule.height / 2;
                        return (
                            line > rect.y + RULE_EDGE_TOL_PX &&
                            line < rect.y + rect.height - RULE_EDGE_TOL_PX
                        );
                    }
                    const shared =
                        Math.min(rule.y + rule.height, rect.y + rect.height) -
                        Math.max(rule.y, rect.y);
                    if (shared < rect.height * RULE_OVERLAP_FRACTION) return false;
                    const line = rule.x + rule.width / 2;
                    return (
                        line > rect.x + RULE_EDGE_TOL_PX &&
                        line < rect.x + rect.width - RULE_EDGE_TOL_PX
                    );
                });
                // A shape and the container holding it are crossed by the same line, and counting
                // both would turn one crossing into the two that make this a finding.
                const crossedShapes = crossed.filter(
                    ({ element }) =>
                        !crossed.some(
                            (other) => other.element !== element && element.contains(other.element),
                        ),
                );
                if (crossedShapes.length < MIN_CROSSED_SHAPES) continue;

                reported.add(candidate.element);
                const first = crossedShapes[0].rect;
                const line =
                    orientation === 'horizontal'
                        ? rule.y + rule.height / 2
                        : rule.x + rule.width / 2;
                const edge =
                    orientation === 'horizontal' ? first.y + first.height : first.x + first.width;
                messages.push(
                    `${describeElement(candidate.element)} passes through ` +
                        `${describeElement(crossedShapes[0].element)} ×${crossedShapes.length} ` +
                        `at progress ` +
                        `${frame.progress} — it sits ${Math.abs(Math.round(edge - line))}px from ` +
                        `their ${orientation === 'horizontal' ? 'bottom' : 'right'} edge rather ` +
                        `than on it. A baseline belongs on the shapes' edge: put the rule and the ` +
                        `shapes in one container and anchor both to the same edge, or mark it ` +
                        `data-l5-allow-intersect if it is meant to cross them.`,
                );
            }
        }
        return messages;
    },
};
